import { useEffect, useState } from 'react'
import { TrendingUpIcon } from 'lucide-react'

import { apiClient } from '@/api/client'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'

/** FIELDS-1 P1a: one captured balance-sheet period, echoed back so the card can
 *  show WHICH years the peg averaged. */
export interface NWCPegPeriod {
  period_label: string
  fiscal_year: number
  accounts_receivable: number | null
  inventory: number | null
  accounts_payable: number | null
  accrued_liabilities: number | null
  cash: number | null
}

export interface NWCPegData {
  deal_id: string
  peg: number | null
  collar_low: number | null
  collar_high: number | null
  periods_used: number
  nwc_by_period: number[]
  note: string
  // FIELDS-1 P1a: present only on a scored response.
  periods?: NWCPegPeriod[]
  average_ar?: number
  average_ap?: number
  average_inventory?: number
  average_accruals?: number
}

interface Props {
  dealId: string
  /** Bumped by the balance-sheet form after a save, to re-read the peg. */
  refreshKey?: number
}

function fmtUsd(n: number | null): string {
  if (n === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

export function NWCPegCard({ dealId, refreshKey = 0 }: Props) {
  const [data, setData] = useState<NWCPegData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const res = await apiClient.get<NWCPegData>(`/qoe/deals/${dealId}/nwc-peg`)
        if (!cancelled) setData(res.data)
      } catch {
        if (!cancelled) setError('Failed to load NWC peg analysis')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [dealId, refreshKey])

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 flex flex-col gap-3">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (!data || data.peg === null) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <p className="text-sm text-muted-foreground">
          {data?.note ?? 'NWC peg analysis not yet available for this deal.'}
        </p>
      </div>
    )
  }

  const bars = data.nwc_by_period.slice(-12)
  const maxAbs = Math.max(1, ...bars.map(v => Math.abs(v)))

  return (
    <div className="rounded-lg border bg-card p-4 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <TrendingUpIcon className="h-4 w-4 text-brand-primary" />
        <span className="font-medium text-sm text-brand-text">
          Recommended NWC Target at Closing
        </span>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <div>
          <p className="text-xs text-muted-foreground">NWC Peg</p>
          <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--color-primary)' }}>
            {fmtUsd(data.peg)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">±10% Collar</p>
          <p className="text-sm font-medium tabular-nums">
            {fmtUsd(data.collar_low)} – {fmtUsd(data.collar_high)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Periods Used</p>
          <p className="text-sm font-medium tabular-nums">{data.periods_used}</p>
        </div>
      </div>

      {bars.length > 0 && (
        <div className="flex items-end gap-1 h-16">
          {bars.map((v, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm bg-brand-primary/70"
              style={{ height: `${Math.max(4, (Math.abs(v) / maxAbs) * 100)}%` }}
              title={fmtUsd(v)}
            />
          ))}
        </div>
      )}

      {/* FIELDS-1 P1a: provenance. The peg is only as trustworthy as the periods
          behind it, so name the fiscal years entered and the component averages
          rather than presenting a single number with no derivation. */}
      {data.periods && data.periods.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-1 pr-3 font-normal">Component</th>
                {data.periods.map(p => (
                  <th key={p.fiscal_year} className="py-1 pr-3 font-normal text-right">
                    {p.period_label}
                  </th>
                ))}
                <th className="py-1 font-normal text-right">Average</th>
              </tr>
            </thead>
            <tbody>
              {([
                ['Accounts Receivable', 'accounts_receivable', data.average_ar],
                ['Inventory', 'inventory', data.average_inventory],
                ['Accounts Payable', 'accounts_payable', data.average_ap],
                ['Accrued Liabilities', 'accrued_liabilities', data.average_accruals],
              ] as const).map(([label, field, avg]) => (
                <tr key={field} className="border-b last:border-0">
                  <td className="py-1 pr-3 whitespace-nowrap">{label}</td>
                  {data.periods!.map(p => (
                    <td key={p.fiscal_year} className="py-1 pr-3 text-right tabular-nums">
                      {fmtUsd(p[field])}
                    </td>
                  ))}
                  <td className="py-1 text-right tabular-nums font-medium">
                    {fmtUsd(avg ?? null)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">{data.note}</p>
    </div>
  )
}

export default NWCPegCard
